import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from '../lib/ClientContext';
import {
  AGE_STEPS,
  CLEAN_FOLDER,
  brandOf,
  cleanableFolder,
  cleanSubject,
  INBOX_ID,
  MAIL_DRAG_TYPE,
  countMatches,
  countQuery,
  loadFolderRoot,
  loadSettings,
  newRule,
  previewMatches,
  readDraggedMail,
  ruleFromMail,
  ruleIsEmpty,
  ruleFolders,
  ruleQuery,
  ruleSummary,
  saveSettings,
  senderFromMail,
  senderSuggestions,
  sourceFolders,
  type AttachState,
  type CleanJob,
  type CleanRule,
  type CleanSettings,
  type DraggedMail,
  type ReadState,
} from '../lib/clean';
import { TOUCH_DROP, TOUCH_LEAVE, TOUCH_OVER } from '../lib/touchDrag';
import { IconBroom, IconPin } from './icons';
import { fmtDay } from '../lib/format';
import './clean.css';

/* ================================================================== */
/* Shared state: settings + the running clean (lives in AppShell, so a */
/* run keeps going while the panel is minimised or closed)             */
/* ================================================================== */

interface RunState {
  running: boolean;
  ruleName: string;
  moved: number;
  /** last finished run, for the status line / toast */
  last?: { moved: number; at: number; auto: boolean; error?: string };
}

interface CleanCtx {
  settings: CleanSettings | undefined;
  loading: boolean;
  save: (s: CleanSettings) => Promise<void>;
  run: RunState;
  runRules: (rules: CleanRule[], auto?: boolean) => Promise<void>;
  cancel: () => void;
  /** a mail to turn into a new rule — set by ruleFromMail, taken by the panel */
  pendingMail: DraggedMail | null;
  takePendingMail: () => void;
  ruleFromMail: (d: DraggedMail) => void;
}
const Ctx = createContext<CleanCtx | null>(null);
function useClean(): CleanCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('CleanProvider missing');
  return c;
}
/** Is a clean currently running? (for the top-bar button) */
export function useCleanRunning(): boolean {
  return useContext(Ctx)?.run.running ?? false;
}
/** Open the panel with a new rule for this mail's sender (tap alternative to dragging). */
export function useCleanRuleFromMail(): ((d: DraggedMail) => void) | undefined {
  return useContext(Ctx)?.ruleFromMail;
}

const AUTO_FIRST_MS = 20_000;
const AUTO_EVERY_MS = 60 * 60_000;
/** status poll while the server runs a clean */
const POLL_MS = 2000;
const PAGE_LOADED = Date.now();

/** /api/clean/{run,status,cancel} — a POST when `body` is given (CSRF header). */
async function cleanApi(path: 'run' | 'status' | 'cancel', csrf?: string, body?: object): Promise<{ status: number; job: CleanJob | null }> {
  const res = await fetch(
    `/api/clean/${path}`,
    body
      ? {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-zwf-csrf': csrf ?? '' },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        }
      : { credentials: 'same-origin', cache: 'no-store' },
  );
  const data = (await res.json().catch(() => ({}))) as { job?: CleanJob | null };
  return { status: res.status, job: data.job ?? null };
}

export function CleanProvider({ children, onOpenPanel }: { children: ReactNode; onOpenPanel: () => void }) {
  const { client, holdRefresh, session } = useClient();
  const qc = useQueryClient();
  const settingsQ = useQuery({ queryKey: ['clean-settings'], queryFn: () => loadSettings(client), staleTime: Infinity });
  const saveM = useMutation({
    mutationFn: (s: CleanSettings) => saveSettings(client, s),
    onMutate: (s) => qc.setQueryData(['clean-settings'], s),
    onError: () => void qc.invalidateQueries({ queryKey: ['clean-settings'] }),
  });
  const [pendingMail, setPendingMail] = useState<DraggedMail | null>(null);

  // the clean itself runs on the server (keeps going with the tab closed); we start it and follow it
  const jobQ = useQuery({
    queryKey: ['clean-job'],
    queryFn: async () => (await cleanApi('status')).job,
    refetchInterval: (q) => (q.state.data?.state === 'running' ? POLL_MS : false),
    staleTime: 0,
  });
  const job = jobQ.data ?? null;
  const jobRunning = job?.state === 'running';
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<{ at: number; error: string } | null>(null);

  // no list/folder refetch wave while the server moves mail — one refresh when it is done
  const sawRunning = useRef(false);
  useEffect(() => {
    if (!jobRunning) return;
    sawRunning.current = true;
    return holdRefresh();
  }, [jobRunning, holdRefresh]);
  useEffect(() => {
    if (jobRunning || !sawRunning.current || !job) return;
    sawRunning.current = false;
    if (job.moved > 0) {
      for (const k of ['conversations', 'messages', 'folders', 'clean-count', 'clean-preview', 'clean-suggest']) {
        void qc.invalidateQueries({ queryKey: [k] });
      }
    }
  }, [jobRunning, job, qc]);

  const run = useMemo<RunState>(() => {
    // a job that finished before this page loaded is old news — no toast for it
    const finished =
      job && job.state !== 'running' && (job.finishedAt ?? 0) > PAGE_LOADED
        ? { moved: job.moved, at: job.finishedAt!, auto: job.auto, error: job.state === 'error' ? job.error : undefined }
        : undefined;
    const last = startError && (!finished || startError.at > finished.at) ? { moved: 0, at: startError.at, auto: false, error: startError.error } : finished;
    return { running: jobRunning || starting, ruleName: jobRunning ? (job?.ruleName ?? '') : '', moved: jobRunning ? (job?.moved ?? 0) : 0, last };
  }, [job, jobRunning, starting, startError]);

  const runRules = useCallback(
    async (rules: CleanRule[], auto = false) => {
      const todo = rules.filter((r) => !ruleIsEmpty(r));
      if (jobRunning || starting || todo.length === 0) return;
      setStarting(true);
      try {
        const r = await cleanApi('run', session.csrfToken, { ruleIds: todo.map((x) => x.id), auto });
        if (r.status === 429) setStartError({ at: Date.now(), error: 'the server is busy with other cleans — try again in a minute' });
        else if (r.status >= 400 && r.status !== 409) setStartError({ at: Date.now(), error: `could not start (HTTP ${r.status})` });
        qc.setQueryData(['clean-job'], r.job); // 409 = already running: follow that one
      } catch {
        setStartError({ at: Date.now(), error: 'could not reach the server' });
      } finally {
        setStarting(false);
      }
    },
    [jobRunning, starting, session.csrfToken, qc],
  );
  const cancel = useCallback(async () => {
    try {
      qc.setQueryData(['clean-job'], (await cleanApi('cancel', session.csrfToken, {})).job);
    } catch {
      /* the next status poll shows the state */
    }
  }, [session.csrfToken, qc]);

  // automatic cleaning while webmail is open: shortly after opening, then hourly
  const settings = settingsQ.data;
  useEffect(() => {
    if (!settings?.auto) return;
    const go = () => void runRules(settings.rules.filter((r) => r.enabled), true);
    const first = setTimeout(go, AUTO_FIRST_MS);
    const every = setInterval(go, AUTO_EVERY_MS);
    return () => {
      clearTimeout(first);
      clearInterval(every);
    };
  }, [settings, runRules]);

  const value = useMemo<CleanCtx>(
    () => ({
      settings,
      loading: settingsQ.isLoading,
      save: async (s) => {
        await saveM.mutateAsync(s);
      },
      run,
      runRules,
      cancel: () => void cancel(),
      pendingMail,
      takePendingMail: () => setPendingMail(null),
      ruleFromMail: (d) => {
        setPendingMail(d);
        onOpenPanel();
      },
    }),
    [settings, settingsQ.isLoading, saveM, run, runRules, cancel, pendingMail, onOpenPanel],
  );
  return (
    <Ctx.Provider value={value}>
      {children}
      <CleanToast />
    </Ctx.Provider>
  );
}

/** "Moved N mails to HouseCleaning" — shown after a run that moved something. */
function CleanToast() {
  const { run } = useClean();
  const [shown, setShown] = useState<number | null>(null);
  useEffect(() => {
    if (!run.last || (run.last.moved === 0 && !run.last.error)) return;
    setShown(run.last.at);
    const t = setTimeout(() => setShown(null), 5000);
    return () => clearTimeout(t);
  }, [run.last]);
  if (!run.last || shown !== run.last.at) return null;
  return (
    <div className={`clean-toast${run.last.error ? ' err' : ''}`} role="status">
      <IconBroom />
      {run.last.error
        ? `Cleaning stopped: ${run.last.error}`
        : `${run.last.auto ? 'Auto-clean moved' : 'Moved'} ${run.last.moved.toLocaleString()} mail${run.last.moved === 1 ? '' : 's'} to ${CLEAN_FOLDER}`}
    </div>
  );
}

/* ================================================================== */
/* Floating panel                                                      */
/* ================================================================== */

const LS_POS = 'clean.pos';
function readPos(): { x: number; y: number } | null {
  try {
    const v = JSON.parse(localStorage.getItem(LS_POS) ?? 'null') as { x: number; y: number } | null;
    return v && typeof v.x === 'number' ? v : null;
  } catch {
    return null;
  }
}

export function CleanPanel({ pinned, onPin, onClose }: { pinned: boolean; onPin: (v: boolean) => void; onClose: () => void }) {
  const { settings, loading, save, run, runRules, cancel, pendingMail, takePendingMail } = useClean();
  const [view, setView] = useState<{ kind: 'list' } | { kind: 'edit'; rule: CleanRule; isNew: boolean }>({ kind: 'list' });
  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState(readPos);
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const rules = settings?.rules ?? [];
  const enabled = rules.filter((r) => r.enabled && !ruleIsEmpty(r));
  const toRule = useMailToRule();
  // a mail dropped on the panel (or the minimised pill) → a new rule for its sender, ready to adjust
  const [dropOver, dropProps] = useMailDrop((d) => {
    setMinimized(false);
    setView({ kind: 'edit', rule: toRule.newRule(d), isNew: true });
  });
  // the panel element is both the move handle's measure and a drop target
  const dropRef = dropProps.ref;
  const setPanelEl = useCallback(
    (el: HTMLDivElement | null) => {
      panelRef.current = el;
      dropRef(el);
    },
    [dropRef],
  );
  // a mail handed over by the "Clean rule" button / menu
  useEffect(() => {
    if (!pendingMail || !toRule.ready) return;
    takePendingMail();
    setMinimized(false);
    setView({ kind: 'edit', rule: toRule.newRule(pendingMail), isNew: true });
  }, [pendingMail, takePendingMail, toRule]);

  function onDragStart(e: React.PointerEvent) {
    if ((e.target as HTMLElement).closest('button') || window.innerWidth < 700) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    drag.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onDragMove(e: React.PointerEvent) {
    if (!drag.current) return;
    const w = panelRef.current?.offsetWidth ?? 400;
    const x = Math.max(8, Math.min(window.innerWidth - w - 8, e.clientX - drag.current.dx));
    const y = Math.max(8, Math.min(window.innerHeight - 60, e.clientY - drag.current.dy));
    setPos({ x, y });
  }
  function onDragEnd() {
    if (!drag.current) return;
    drag.current = null;
    try {
      localStorage.setItem(LS_POS, JSON.stringify(pos));
    } catch {
      /* private mode — position just isn't remembered */
    }
  }

  async function upsert(rule: CleanRule) {
    if (!settings) return;
    const exists = settings.rules.some((r) => r.id === rule.id);
    await save({ ...settings, rules: exists ? settings.rules.map((r) => (r.id === rule.id ? rule : r)) : [...settings.rules, rule] });
  }
  async function remove(id: string) {
    if (!settings) return;
    await save({ ...settings, rules: settings.rules.filter((r) => r.id !== id) });
  }

  if (minimized) {
    return (
      <button
        type="button"
        className={`clean-pill${dropOver ? ' drop-over' : ''}`}
        onClick={() => setMinimized(false)}
        title="Open Clean inbox — or drop a mail here to make a rule"
        {...dropProps}
      >
        <IconBroom />
        {run.running ? (
          <span>
            Cleaning… <b>{run.moved.toLocaleString()}</b>
          </span>
        ) : (
          <span>Clean inbox</span>
        )}
        {run.running ? <span className="clean-pill-spin" /> : null}
      </button>
    );
  }

  const style = pos ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined;
  return (
    <div
      {...(view.kind === 'list' ? dropProps : {})}
      ref={setPanelEl}
      className={`clean-panel${dropOver && view.kind === 'list' ? ' drop-over' : ''}`}
      style={style}
      role="dialog"
      aria-label="Clean inbox"
    >
      <div className="clean-hdr" onPointerDown={onDragStart} onPointerMove={onDragMove} onPointerUp={onDragEnd}>
        <span className="clean-hdr-icon">
          <IconBroom />
        </span>
        <span className="clean-hdr-title">Clean inbox</span>
        <button
          type="button"
          className={`clean-hdr-btn${pinned ? ' on' : ''}`}
          title={pinned ? 'Pinned — stays open, also after reload' : 'Pin — keep this panel open'}
          aria-pressed={pinned}
          onClick={() => onPin(!pinned)}
        >
          <IconPin />
        </button>
        <button type="button" className="clean-hdr-btn" title="Minimise" onClick={() => setMinimized(true)}>
          –
        </button>
        <button type="button" className="clean-hdr-btn" title="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      {loading || !settings ? (
        <div className="clean-body">
          <div className="clean-muted">Loading…</div>
        </div>
      ) : view.kind === 'edit' ? (
        <RuleEditor
          rule={view.rule}
          isNew={view.isNew}
          onCancel={() => setView({ kind: 'list' })}
          onSave={async (r) => {
            await upsert(r);
            setView({ kind: 'list' });
          }}
          onDelete={async () => {
            await remove(view.rule.id);
            setView({ kind: 'list' });
          }}
        />
      ) : (
        <>
          <RunBar rules={enabled} run={run} onRun={() => void runRules(enabled)} onCancel={cancel} />
          <div className="clean-body">
            {dropOver ? <div className="clean-drop-hint">Drop the mail to make a rule for its sender — or drop it on a rule to add the sender there</div> : null}
            <div className="clean-section-hdr">
              <span>Your rules</span>
              <button type="button" className="clean-link" onClick={() => setView({ kind: 'edit', rule: newRule(), isNew: true })}>
                + New rule
              </button>
            </div>
            {rules.length === 0 ? (
              <div className="clean-empty">
                <IconBroom />
                <div>
                  <b>No rules yet.</b> Pick a frequent sender below, or create your own rule — e.g. “everything from PayPal”
                  or “read newsletters older than a month”.
                </div>
              </div>
            ) : (
              <div className="clean-rules">
                {rules.map((r) => (
                  <RuleCard
                    key={r.id}
                    rule={r}
                    busy={run.running}
                    onToggle={() => void upsert({ ...r, enabled: !r.enabled })}
                    onEdit={() => setView({ kind: 'edit', rule: r, isNew: false })}
                    onRun={() => void runRules([r])}
                    onMail={(d) =>
                      setView({
                        kind: 'edit',
                        rule: { ...r, senders: addOnce(r.senders, toRule.sender(d)), folders: addOnce(ruleFolders(r), toRule.folder(d)) },
                        isNew: false,
                      })
                    }
                  />
                ))}
              </div>
            )}
            <Suggestions
              rules={rules}
              onAdd={(r) => void upsert(r)}
              onCustomise={(r) => setView({ kind: 'edit', rule: r, isNew: true })}
            />
          </div>
          <div className="clean-foot">
            <label className="clean-auto">
              <Switch on={settings.auto} onChange={(v) => void save({ ...settings, auto: v })} />
              <span>
                <b>Clean automatically</b>
                <small>when webmail opens and every hour while it’s open</small>
              </span>
            </label>
            <div className="clean-note">
              Flagged mail is never moved · nothing is deleted · moved mail is in “{CLEAN_FOLDER}/&lt;folder it came from&gt;” · tip: drag a
              mail here to make a rule
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`clean-switch${on ? ' on' : ''}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(!on);
      }}
    >
      <span />
    </button>
  );
}

/** Sender logo (badge server) with a coloured initial as fallback. */
function SenderLogo({ text, email }: { text: string; email?: string }) {
  const [ok, setOk] = useState<boolean | null>(null);
  const hue = useMemo(() => [...text].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7), [text]);
  return (
    <span className="clean-logo" style={{ background: ok ? '#fff' : `hsl(${hue} 62% 52%)` }}>
      {ok ? null : <span>{(text.trim()[0] ?? '?').toUpperCase()}</span>}
      {email && ok !== false ? (
        <img src={`/api/badge?email=${encodeURIComponent(email)}`} alt="" loading="lazy" onLoad={() => setOk(true)} onError={() => setOk(false)} />
      ) : null}
    </span>
  );
}
/** a representative sender address for a rule's logo */
function logoEmail(r: CleanRule): string | undefined {
  const s = r.senders.find((x) => x.trim())?.trim().toLowerCase();
  if (!s) return undefined;
  if (s.includes('@') && !s.startsWith('@')) return s;
  if (s.startsWith('@')) return `info${s}`;
  if (s.includes('.')) return `info@${s}`;
  return r.logo;
}

function useRuleCount(rule: CleanRule) {
  const { client } = useClient();
  return useQuery({
    queryKey: ['clean-count', ruleQuery(rule)],
    queryFn: () => countMatches(client, rule),
    enabled: !ruleIsEmpty(rule),
    staleTime: 60_000,
  });
}

/** Folders a rule can clean — own folders plus shares we may delete from (tree loaded with mountpoints traversed). */
function useSourceFoldersQ() {
  const { client } = useClient();
  const q = useQuery({ queryKey: ['folders', 'clean'], queryFn: () => loadFolderRoot(client), staleTime: 5 * 60_000 });
  const list = useMemo(() => sourceFolders(q.data), [q.data]);
  return { list, ready: q.isSuccess };
}
function useSourceFolders() {
  return useSourceFoldersQ().list;
}

/** What a dropped mail turns into: our own domain (personal senders there stay full addresses) + cleanable folder ids. */
function useMailToRule() {
  const { session } = useClient();
  const own = session.user.split('@')[1] ?? '';
  const { list: sources, ready } = useSourceFoldersQ();
  return useMemo(
    () => ({
      /** folders known — before that a mail's folder would fall back to Inbox */
      ready,
      newRule: (d: DraggedMail) => ruleFromMail(d, own, sources),
      sender: (d: DraggedMail) => senderFromMail(d.from, own),
      folder: (d: DraggedMail) => cleanableFolder(sources, d.folderId),
    }),
    [own, sources, ready],
  );
}

/** Drop target for a mail dragged from the list (mouse, or touch-and-hold); `over` while one hovers it. */
function useMailDrop(onMail: (d: DraggedMail) => void) {
  const [over, setOver] = useState(false);
  const [el, setEl] = useState<HTMLElement | null>(null);
  const cb = useRef(onMail);
  cb.current = onMail;
  useEffect(() => {
    if (!el) return;
    const on = () => setOver(true);
    const off = () => setOver(false);
    const drop = (e: Event) => {
      setOver(false);
      const d = (e as CustomEvent<DraggedMail | null>).detail;
      if (d) cb.current(d);
    };
    el.addEventListener(TOUCH_OVER, on);
    el.addEventListener(TOUCH_LEAVE, off);
    el.addEventListener(TOUCH_DROP, drop);
    return () => {
      el.removeEventListener(TOUCH_OVER, on);
      el.removeEventListener(TOUCH_LEAVE, off);
      el.removeEventListener(TOUCH_DROP, drop);
    };
  }, [el]);
  useEffect(() => {
    // capture phase: a nested target stops propagation of the drop it handles
    const off = () => setOver(false);
    document.addEventListener('dragend', off, true);
    document.addEventListener('drop', off, true);
    return () => {
      document.removeEventListener('dragend', off, true);
      document.removeEventListener('drop', off, true);
    };
  }, []);
  const props = {
    ref: setEl,
    'data-mail-drop': '',
    onDragOver: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(MAIL_DRAG_TYPE)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
      setOver(true);
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
    },
    onDrop: (e: React.DragEvent) => {
      const d = readDraggedMail(e.dataTransfer);
      if (!d) return;
      e.preventDefault();
      e.stopPropagation();
      setOver(false);
      cb.current(d);
    },
  };
  return [over, props] as const;
}

/** add a value to a list once */
const addOnce = <T,>(list: T[], v: T | undefined) => (v === undefined || list.includes(v) ? list : [...list, v]);

function RunBar({ rules, run, onRun, onCancel }: { rules: CleanRule[]; run: RunState; onRun: () => void; onCancel: () => void }) {
  const { client } = useClient();
  const totals = useQuery({
    queryKey: ['clean-count', 'total', rules.map((r) => ruleQuery(r)).join('|')],
    queryFn: async () => {
      // rules can overlap; count the union via one OR'ed query (each part carries its own folders)
      const live = rules.filter((r) => ruleFolders(r).length > 0);
      if (live.length === 0) return { n: 0, capped: false };
      return countQuery(client, live.map((r) => `(${ruleQuery(r)})`).join(' OR '));
    },
    staleTime: 60_000,
  });
  const n = totals.data?.n ?? 0;
  const capped = !!totals.data?.capped;
  let status: ReactNode;
  if (run.running) {
    status = (
      <>
        Moving <b>{run.moved.toLocaleString()}</b>
        {n && !capped ? ` of ~${n.toLocaleString()}` : ''} · {run.ruleName || 'rule'}
      </>
    );
  } else if (rules.length === 0) {
    status = 'Turn on a rule to start cleaning';
  } else if (totals.isLoading) {
    status = 'Counting…';
  } else if (n === 0) {
    status = run.last && !run.last.error ? 'Inbox is clean ✓' : 'Nothing to move right now';
  } else {
    status = (
      <>
        <b>
          {n.toLocaleString()}
          {totals.data?.capped ? '+' : ''}
        </b>{' '}
        mail{n === 1 ? '' : 's'} ready to move
      </>
    );
  }
  const pct = run.running && n && !capped ? Math.min(100, Math.round((run.moved / n) * 100)) : 0;
  return (
    <div className="clean-run">
      <div className="clean-run-text">{status}</div>
      {run.running ? (
        <button type="button" className="ghost" onClick={onCancel}>
          Stop
        </button>
      ) : (
        <button type="button" className="primary" disabled={n === 0} onClick={onRun}>
          Clean now
        </button>
      )}
      {run.running ? (
        <div className="clean-progress">
          <span style={{ width: `${pct || 8}%` }} className={pct ? '' : 'indeterminate'} />
        </div>
      ) : null}
    </div>
  );
}

/** "Inbox, ap@mitral.dk" — empty for the default (Inbox only). */
function useFolderLabel(rule: CleanRule): string {
  const sources = useSourceFolders();
  const ids = ruleFolders(rule);
  if (ids.length === 1 && ids[0] === INBOX_ID) return '';
  const byId = new Map(sources.map((f) => [f.id, f.path]));
  return ids.map((id) => byId.get(id) ?? '(deleted folder)').join(', ');
}

/** Source-folder chips + an "add folder" picker. */
function FolderPicker({ value, onChange, drop }: { value: string[]; onChange: (v: string[]) => void; drop: ReturnType<typeof useMailDrop> }) {
  const all = useSourceFolders();
  const byId = new Map(all.map((f) => [f.id, f]));
  const left = all.filter((f) => !value.includes(f.id));
  const [over, dropProps] = drop;
  return (
    <div className={`clean-field${over ? ' drop-over' : ''}`} {...dropProps}>
      <span>Clean folders</span>
      <div className="clean-tags">
        {value.map((id) => (
          <span key={id} className="clean-tag">
            {byId.get(id)?.path ?? '(deleted folder)'}
            <button type="button" aria-label={`Remove ${byId.get(id)?.path ?? id}`} onClick={() => onChange(value.filter((x) => x !== id))}>
              ✕
            </button>
          </span>
        ))}
        {left.length ? (
          <select
            className="clean-folder-add"
            value=""
            aria-label="Add a folder"
            onChange={(e) => e.target.value && onChange([...value, e.target.value])}
          >
            <option value="">{value.length ? 'add folder…' : 'choose a folder…'}</option>
            {left.map((f) => (
              <option key={f.id} value={f.id}>
                {'\u00a0\u00a0'.repeat(f.depth)}
                {f.path.split('/').pop()}
                {f.shared ? ' · shared' : ''}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <small>
        Mail moves to {CLEAN_FOLDER}/&lt;folder&gt;, e.g. Inbox → {CLEAN_FOLDER}/Inbox. Sub-folders are only cleaned when added
        themselves. Shared folders are listed when you may delete in them.
      </small>
    </div>
  );
}

function RuleCard({
  rule,
  busy,
  onToggle,
  onEdit,
  onRun,
  onMail,
}: {
  rule: CleanRule;
  busy: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onRun: () => void;
  onMail: (d: DraggedMail) => void;
}) {
  const count = useRuleCount(rule);
  const n = count.data?.n;
  const where = useFolderLabel(rule);
  const [over, dropProps] = useMailDrop(onMail);
  return (
    <div
      className={`clean-rule${rule.enabled ? '' : ' off'}${over ? ' drop-over' : ''}`}
      onClick={onEdit}
      role="button"
      tabIndex={0}
      title="Edit — or drop a mail here to add its sender"
      {...dropProps}
    >
      <SenderLogo text={rule.name || rule.senders[0] || '?'} email={logoEmail(rule)} />
      <div className="clean-rule-main">
        <div className="clean-rule-name">{rule.name || ruleSummary(rule)[0] || 'Untitled rule'}</div>
        <div className="clean-chips">
          {where ? <span className="clean-chip">in {where}</span> : null}
          {ruleSummary(rule).map((s) => (
            <span key={s} className="clean-chip">
              {s}
            </span>
          ))}
        </div>
      </div>
      <div className="clean-rule-side" onClick={(e) => e.stopPropagation()}>
        <span className={`clean-count${n ? ' has' : ''}`} title={`Matching mails in ${where || 'Inbox'}`}>
          {count.isLoading ? '…' : n === undefined ? '–' : `${n.toLocaleString()}${count.data?.capped ? '+' : ''}`}
        </span>
        <button type="button" className="clean-mini" disabled={busy || !n} title="Run this rule now" onClick={onRun}>
          Run
        </button>
        <Switch on={rule.enabled} onChange={onToggle} label="Rule on/off" />
      </div>
    </div>
  );
}

function Suggestions({ rules, onAdd, onCustomise }: { rules: CleanRule[]; onAdd: (r: CleanRule) => void; onCustomise: (r: CleanRule) => void }) {
  const { client, session } = useClient();
  const [more, setMore] = useState(false);
  const own = session.user.split('@')[1] ?? '';
  const q = useQuery({ queryKey: ['clean-suggest', own], queryFn: () => senderSuggestions(client, own), staleTime: 10 * 60_000 });
  const covered = new Set(rules.flatMap((r) => r.senders.map((s) => brandOf(s.replace(/^.*@/, '').toLowerCase()))));
  const list = (q.data ?? []).filter((s) => s.count >= 3 && !s.brands.some((b) => covered.has(b)));
  const shown = more ? list.slice(0, 30) : list.slice(0, 6);
  const ruleFor = (s: (typeof list)[number]) => newRule({ name: `All ${s.name}`, senders: s.brands, logo: s.sample });
  return (
    <>
      <div className="clean-section-hdr">
        <span>Frequent senders</span>
        <small>from your newest 1,000 Inbox mails</small>
      </div>
      {q.isLoading ? <div className="clean-muted">Looking through your Inbox…</div> : null}
      {!q.isLoading && list.length === 0 ? <div className="clean-muted">No frequent senders without a rule.</div> : null}
      <div className="clean-suggest">
        {shown.map((s) => (
          <div key={s.brand} className="clean-sugg">
            <SenderLogo text={s.name} email={s.sample} />
            <div className="clean-sugg-main" title={`Matches every sender with “${s.brands.join('” or “')}” in the name or address`}>
              <div className="clean-sugg-name">{s.name}</div>
              <div className="clean-sugg-sub">
                {s.domain} · {s.count} recent
              </div>
            </div>
            <button type="button" className="clean-mini" title="Adjust before adding" onClick={() => onCustomise(ruleFor(s))}>
              Adjust
            </button>
            <button type="button" className="clean-mini accent" title={`Move all ${s.name} mail to ${CLEAN_FOLDER}`} onClick={() => onAdd(ruleFor(s))}>
              + Rule
            </button>
          </div>
        ))}
      </div>
      {list.length > 6 ? (
        <button type="button" className="clean-link center" onClick={() => setMore(!more)}>
          {more ? 'Show fewer' : `Show ${Math.min(30, list.length) - 6} more`}
        </button>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */

function Segmented<T extends string | number>({ value, options, onChange }: { value: T; options: Array<{ v: T; label: string }>; onChange: (v: T) => void }) {
  return (
    <div className="clean-seg" role="radiogroup">
      {options.map((o) => (
        <button key={String(o.v)} type="button" role="radio" aria-checked={value === o.v} className={value === o.v ? 'on' : ''} onClick={() => onChange(o.v)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function RuleEditor({
  rule,
  isNew,
  onCancel,
  onSave,
  onDelete,
}: {
  rule: CleanRule;
  isNew: boolean;
  onCancel: () => void;
  onSave: (r: CleanRule) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const { client } = useClient();
  const [r, setR] = useState<CleanRule>(rule);
  const [who, setWho] = useState('');
  const [moreOpen, setMoreOpen] = useState(!!(rule.largerMb || rule.query));
  const [saving, setSaving] = useState(false);
  const set = (p: Partial<CleanRule>) => setR((cur) => ({ ...cur, ...p }));

  // a mail dropped on a field fills that field; anywhere else adds its sender
  const toRule = useMailToRule();
  const addSender = (d: DraggedMail) => setR((cur) => ({ ...cur, senders: addOnce(cur.senders, toRule.sender(d)) }));
  const bodyDrop = useMailDrop(addSender);
  const fromDrop = useMailDrop(addSender);
  const subjectDrop = useMailDrop((d) => d.subject && set({ subject: cleanSubject(d.subject) }));
  const folderDrop = useMailDrop((d) => setR((cur) => ({ ...cur, folders: addOnce(ruleFolders(cur), toRule.folder(d)) })));

  // the rule as it would be saved (a typed-but-not-entered sender counts too)
  const effective = useMemo(() => (who.trim() ? { ...r, senders: [...r.senders, who.trim()] } : r), [r, who]);
  const [debounced, setDebounced] = useState(effective);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(effective), 350);
    return () => clearTimeout(t);
  }, [effective]);
  const empty = ruleIsEmpty(debounced) || ruleFolders(debounced).length === 0;
  const count = useQuery({
    queryKey: ['clean-count', ruleQuery(debounced)],
    queryFn: () => countMatches(client, debounced),
    enabled: !empty,
    staleTime: 60_000,
  });
  const preview = useQuery({
    queryKey: ['clean-preview', ruleQuery(debounced)],
    queryFn: () => previewMatches(client, debounced),
    enabled: !empty,
    staleTime: 60_000,
  });

  function addWho() {
    const v = who.trim().replace(/,$/, '');
    if (v && !r.senders.includes(v)) set({ senders: [...r.senders, v] });
    setWho('');
  }
  async function submit() {
    const final = { ...effective, senders: effective.senders.filter((s) => s.trim()) };
    if (ruleIsEmpty(final) || ruleFolders(final).length === 0) return;
    if (!final.name.trim()) final.name = ruleSummary(final)[0] ?? 'Rule';
    setSaving(true);
    try {
      await onSave(final);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className={`clean-body clean-edit${bodyDrop[0] ? ' drop-over' : ''}`} {...bodyDrop[1]}>
        <button type="button" className="clean-link back" onClick={onCancel}>
          ‹ Rules
        </button>
        <label className="clean-field">
          <span>Rule name</span>
          <input value={r.name} placeholder="e.g. All PayPal" onChange={(e) => set({ name: e.target.value })} />
        </label>

        <FolderPicker value={ruleFolders(r)} onChange={(folders) => set({ folders })} drop={folderDrop} />

        <div className={`clean-field${fromDrop[0] ? ' drop-over' : ''}`} {...fromDrop[1]}>
          <span>From</span>
          <div className="clean-tags" onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus()}>
            {r.senders.map((s) => (
              <span key={s} className="clean-tag">
                {s}
                <button type="button" aria-label={`Remove ${s}`} onClick={() => set({ senders: r.senders.filter((x) => x !== s) })}>
                  ✕
                </button>
              </span>
            ))}
            <input
              value={who}
              placeholder={r.senders.length ? 'add another…' : 'paypal, linkedin.com, news@shop.dk'}
              onChange={(e) => setWho(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addWho();
                } else if (e.key === 'Backspace' && !who && r.senders.length) {
                  set({ senders: r.senders.slice(0, -1) });
                }
              }}
              onBlur={addWho}
            />
          </div>
          <small>
            A name like “paypal” catches all PayPal mail, whatever the address. Leave empty to match any sender. Drop a mail here to add its
            sender.
          </small>
        </div>

        <div className="clean-field">
          <span>Older than</span>
          <Segmented value={r.olderDays ?? 0} options={AGE_STEPS.map((s) => ({ v: s.days, label: s.label }))} onChange={(v) => set({ olderDays: v })} />
        </div>
        <div className="clean-row2">
          <div className="clean-field">
            <span>Status</span>
            <Segmented<ReadState>
              value={r.read ?? 'any'}
              options={[
                { v: 'any', label: 'Any' },
                { v: 'read', label: 'Read' },
                { v: 'unread', label: 'Unread' },
              ]}
              onChange={(v) => set({ read: v })}
            />
          </div>
          <div className="clean-field">
            <span>Attachments</span>
            <Segmented<AttachState>
              value={r.attach ?? 'any'}
              options={[
                { v: 'any', label: 'Any' },
                { v: 'yes', label: 'With' },
                { v: 'no', label: 'Without' },
              ]}
              onChange={(v) => set({ attach: v })}
            />
          </div>
        </div>
        <label className={`clean-field${subjectDrop[0] ? ' drop-over' : ''}`} {...subjectDrop[1]}>
          <span>Subject contains</span>
          <input value={r.subject ?? ''} placeholder="optional, e.g. receipt" onChange={(e) => set({ subject: e.target.value })} />
        </label>

        <button type="button" className="clean-link" onClick={() => setMoreOpen(!moreOpen)}>
          {moreOpen ? '▾' : '▸'} More options
        </button>
        {moreOpen ? (
          <div className="clean-more">
            <label className="clean-field">
              <span>Larger than (MB)</span>
              <input
                type="number"
                min={0}
                step={1}
                value={r.largerMb ?? ''}
                placeholder="any size"
                onChange={(e) => set({ largerMb: e.target.value ? Math.max(0, Number(e.target.value)) : undefined })}
              />
            </label>
            <label className="clean-field">
              <span>Advanced search</span>
              <input value={r.query ?? ''} placeholder='Zimbra search, e.g. to:info@mo2.dk' onChange={(e) => set({ query: e.target.value })} />
            </label>
            <code className="clean-query">{ruleQuery(effective)}</code>
          </div>
        ) : null}

        <div className="clean-preview">
          {empty ? (
            <div className="clean-muted">
              {ruleFolders(debounced).length === 0
                ? 'Choose at least one folder to clean.'
                : 'Add at least one condition to see what this rule moves.'}
            </div>
          ) : (
            <>
              <div className="clean-preview-hdr">
                {count.isLoading ? (
                  'Counting…'
                ) : count.isError ? (
                  <span className="clean-err">That search isn’t valid — check “Advanced search”.</span>
                ) : (
                  <>
                    <b>
                      {(count.data?.n ?? 0).toLocaleString()}
                      {count.data?.capped ? '+' : ''}
                    </b>{' '}
                    mail{count.data?.n === 1 ? '' : 's'} match
                  </>
                )}
              </div>
              {(preview.data ?? []).map((m) => (
                <div key={m.id} className="clean-preview-row">
                  <span className="clean-preview-from">{m.from}</span>
                  <span className="clean-preview-su">{m.subject}</span>
                  <span className="clean-preview-d">{fmtDay(m.date)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      <div className="clean-foot clean-edit-foot">
        {!isNew ? (
          <button type="button" className="ghost danger" onClick={() => void onDelete()}>
            Delete
          </button>
        ) : (
          <span />
        )}
        <div className="clean-edit-actions">
          <button type="button" className="ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="primary" disabled={saving || ruleIsEmpty(effective) || ruleFolders(effective).length === 0} onClick={() => void submit()}>
            {saving ? 'Saving…' : isNew ? 'Add rule' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}
